import { closureButtonClass } from "../../utils/closureButtonStyle";
import { createPortal } from "react-dom";
import { knowAcces } from "../../utils/utils";
import { rigaNellaFattura, normalizzaCodicePrestazione, isRigaSezioneTreUnificabile } from "../../utils/invoiceDiscounts";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GridToolbarContainer, GridToolbarQuickFilter, itIT } from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import LinearProgress from "@mui/material/LinearProgress";
import api from "../../api/api";
import { toast } from "react-toastify";
import { Autocomplete } from "../../components/Autocomplete";
import { CustomFooterPersonalized } from "../../components/DataGrid/CustomFooterPersonalized";
import Modal from "../../components/Modal";
import { formatItalianNumber } from "../../utils/formatItalianNumber";
import * as XLSX from "xlsx-js-style";
import { codificaWindows1252, creaCsvValorizzazioniAgo } from "../../utils/exportValorizzazioniAgo";


const euro = (value) => formatItalianNumber(value, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}) + "\u00a0€";
const quantita = (value) => formatItalianNumber(value, { maximumFractionDigits: 2 });
const TIPI_FATTURAZIONE = [
  ["Z", "bg-blue-100 text-blue-700 border-blue-200"],
  ["I", "bg-green-100 text-green-700 border-green-200"],
  ["B", "bg-purple-100 text-purple-700 border-purple-200"],
];
const VISTA_FATTURAZIONE_KEY = "parcellazione.fatturazione.vista";
const SEZIONI_LABEL = {
  0: "Payroll BPoint",
  1: "Payroll Zucchetti",
  2: "Payroll iiPay",
  3: "Altri adempimenti",
  4: "Forfait consulenza",
  5: "Forfait F24",
  6: "Import file",
  7: "Sconto cliente",
};
const MESI_LABEL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const getGruppoRiga = (row) => {
  if (row.tipo === "II") {
    const match = String(row.gruppo_fatturazione || row.codice_parcellazione || "").match(/^(II-\d+-[A-Z]+)(?:\d+)?$/i);
    return match ? { id: match[1].toUpperCase(), label: match[1].toUpperCase(), tipo: "I" } : null;
  }
  if (row.tipo === "Z" && /\bCALVI\b/i.test(row.cliente)) {
    return { id: `Z-${row.codice_parcellazione}`, label: `CALVI · ${row.codice_parcellazione}`, tipo: "Z" };
  }
  if (row.tipo === "Z" && /STUDIO\s+MMVN/i.test(row.cliente)) {
    return { id: `Z-${row.codice_parcellazione}`, label: `Studio MMVN · ${row.codice_parcellazione}`, tipo: "Z" };
  }
  return null;
};

const parseCodiceIipay = (codice) => {
  const match = String(codice || "").trim().match(/^I{1,2}-(\d+)-([A-Z]+)(\d+)$/i);
  if (!match) return null;
  return {
    cliente: match[1],
    gruppo: match[2].toUpperCase(),
    specifica: match[3],
    completo: `II-${match[1]}-${match[2].toUpperCase()}${match[3]}`,
    chiaveGruppo: `II-${match[1]}-${match[2].toUpperCase()}`,
  };
};

export const descrizioneFattura = (row, mostraCompetenzaAutomatica = false) => {
  if (row.descrizione_ago_payroll_bpoint != null) return row.descrizione_ago_payroll_bpoint;
  if (row.descrizione_ago_payroll_calvi != null) return row.descrizione_ago_payroll_calvi;
  const ago = String(row.descrizione_voce_ago || row.descrizione_prestazione || "").trim();
  const obbligatoria = String(row.descrizione_extra_fattura || "").trim();
  let descrizione = !obbligatoria || ago.toLowerCase().includes(obbligatoria.toLowerCase())
    ? ago || obbligatoria
    : `${ago} - ${obbligatoria}`;

  const competenzaAutomatica = mostraCompetenzaAutomatica && Number(row.mese_competenza_automatico) === 1;
  const meseCompetenza = Number(row.mese_competenza || row.periodo_mese);
  const annoCompetenza = Number(row.anno_competenza || row.periodo_anno);
  const meseLabel = MESI_LABEL[meseCompetenza - 1];
  let competenza = "";
  if (mostraCompetenzaAutomatica && row.sezione === "import_file" && ["Z", "B"].includes(row.tipologia_fatturazione) && annoCompetenza) {
    competenza = `Anno di competenza: ${annoCompetenza}`;
  } else if (competenzaAutomatica && row.sezione === "forfait_f24" && row.periodo_anno) {
    competenza = `Competenza: ${row.periodo_anno}`;
  } else if (competenzaAutomatica && row.sezione === "forfait_consulenza" && row.periodicita_forfait === "annuale" && row.periodo_anno) {
    competenza = `Competenza: ${row.periodo_anno}`;
  } else if (competenzaAutomatica && ["altri_adempimenti", "forfait_consulenza"].includes(row.sezione) && meseLabel && annoCompetenza) {
    competenza = `Mese di competenza: ${meseLabel.toLowerCase()} ${annoCompetenza}`;
  }
  if (competenza && !descrizione.includes(competenza)) {
    descrizione = descrizione ? `${descrizione} - ${competenza}` : competenza;
  }
  return descrizione;
};

const aggregaDescrizioniBase = (descrizioni) => {
  if (descrizioni.length <= 1) return descrizioni[0] || "";
  const parti = descrizioni.map((value) => value.split(" - ").map((item) => item.trim()));
  let comuni = 0;
  while (parti.every((items) => items[comuni] && items[comuni] === parti[0][comuni])) comuni++;
  if (!comuni) return descrizioni.join("; ");
  const base = parti[0].slice(0, comuni).join(" - ");
  const particolarita = [...new Set(parti.map((items) => items.slice(comuni).join(" - ")).filter(Boolean))];
  return particolarita.length ? `${base} - ${particolarita.join("; ")}` : base;
};

export const aggregaDescrizioni = (values) => {
  const descrizioni = values.filter(Boolean).map((value) => String(value).trim());
  if (descrizioni.length > 1 && descrizioni.every((value) => value === descrizioni[0])) {
    return descrizioni[0];
  }
  const conCompetenza = descrizioni.map((descrizione) => {
    const match = descrizione.match(/^(.*?)(?:\s+-\s+)?((?:Mese di competenza|Competenza):\s+.+)$/i);
    return match
      ? { descrizione: match[1].trim(), competenza: match[2].trim() }
      : { descrizione, competenza: "" };
  });
  const competenze = [...new Set(conCompetenza.map((item) => item.competenza).filter(Boolean))];
  const competenzaComune = competenze.length === 1
    && conCompetenza.every((item) => item.competenza === competenze[0])
    ? competenze[0]
    : "";
  if (!competenzaComune) return aggregaDescrizioniBase(descrizioni);

  const descrizioniSenzaCompetenza = [...new Set(conCompetenza
    .map((item) => item.descrizione)
    .filter(Boolean))];
  const descrizioneAggregata = aggregaDescrizioniBase(descrizioniSenzaCompetenza);
  return descrizioneAggregata ? `${descrizioneAggregata} - ${competenzaComune}` : competenzaComune;
};

const rimuoviCodiceFinale = (descrizione, codice) => {
  if (!codice || !descrizione.toUpperCase().endsWith(String(codice).toUpperCase())) return descrizione;
  return descrizione.slice(0, -String(codice).length).replace(/\s+-\s*$/, "").trim();
};

const sezioneNumero = (value) => ({
  payroll_bpoint: 0,
  payroll_zucchetti: 1,
  payroll_iipay: 2,
  altri_adempimenti: 3,
  forfait_consulenza: 4,
  forfait_f24: 5,
  import_file: 6,
  sconto_cliente: 7,
}[value] ?? "—");

export const destinazioneFatturazione = (row, anno, mese) => {
  if (row.sezione === "payroll_bpoint") return null;
  const trimestraleBpoint = row.tipologia_fatturazione === "B"
    && ["altri_adempimenti", "forfait_consulenza", "forfait_f24", "import_file"].includes(row.sezione);
  // Queste voci vengono gia generate nel mese in cui saranno fatturate.
  if (!trimestraleBpoint && (String(row.prestazione) === "186"
    || ["payroll_iipay", "forfait_f24"].includes(row.sezione))) return null;

  const periodicita = String(
    trimestraleBpoint ? "trimestrale" : row.sezione === "forfait_consulenza"
      ? row.periodicita_cliente_fatturazione
      : row.periodicita_fatturazione
    || "mensile"
  ).toLowerCase();
  let mesiFatturazione;
  switch (periodicita) {
    case "trimestrale": mesiFatturazione = [1, 4, 7, 10]; break;
    case "semestrale": mesiFatturazione = [1, 7]; break;
    case "annuale": mesiFatturazione = [4]; break;
    case "custom":
      mesiFatturazione = [...new Set(String(row.mesi_fatturazione_periodicita || "")
        .split(",").map(Number).filter((value) => value >= 1 && value <= 12))]
        .sort((a, b) => a - b);
      break;
    default: return null;
  }
  if (!mesiFatturazione.length) return null;

  const meseDestinazione = mesiFatturazione.find((value) => trimestraleBpoint ? value > mese : value >= mese)
    || mesiFatturazione[0];
  const annoDestinazione = meseDestinazione < mese ? anno + 1 : anno;
  if (meseDestinazione === mese && annoDestinazione === anno) return null;
  return `${MESI_LABEL[meseDestinazione - 1]} ${annoDestinazione}`;
};

export const aggregaRighe = (lines, periodoSelezionato = null, mostraCompetenzaAutomatica = false, mostraSconti = false) => {
  const specialiZucchetti = new Map();
  lines.forEach((row) => {
    if (row.tipologia_fatturazione !== "Z" || !row.codice_parcellazione) return;
    const nome = String(row.ragione_sociale || "").trim();
    if (/\bCALVI\b/i.test(nome) || /STUDIO\s+MMVN/i.test(nome)) {
      specialiZucchetti.set(String(row.codice_parcellazione), nome);
    }
  });
  const groups = new Map();
  const occurrences = new Map();
  lines.filter((row) => !["esclusa", "eliminata"].includes(row.stato)
    && (mostraSconti || (row.sezione !== "sconto_cliente" && row.origine !== "sconto"))
    && (mostraCompetenzaAutomatica || row.tipologia_fatturazione !== "B" || (row.sezione !== "sconto_cliente" && row.origine !== "sconto"))
    && Number(row.totale || 0) !== 0).forEach((row) => {
    const iipay = row.tipologia_fatturazione === "I" ? parseCodiceIipay(row.codice_parcellazione) : null;
    const principaleZucchetti = row.tipologia_fatturazione === "Z"
      ? specialiZucchetti.get(String(row.codice_parcellazione))
      : null;
    // Le voci uguali si aggregano solo all'interno della singola azienda.
    // Il gruppo serve esclusivamente come filtro/contenitore di fattura:
    // società diverse devono restare su righe distinte anche a parità di voce.
    const soggetto = row.codice_univoco_azienda;
    const codicePrestazione = normalizzaCodicePrestazione(row.nuovo_codice_ago || row.prestazione) || "—";
    const descrizioneBase = descrizioneFattura(row, mostraCompetenzaAutomatica);
    // Nei gruppi di fatturazione speciali (es. CALVI/Studio MMVN) la ragione
    // sociale è già esposta nella colonna Cliente e non va ripetuta in descrizione.
    const codiceDescrizione = iipay?.cliente === "425"
      ? `${iipay.gruppo}${iipay.specifica}`
      : principaleZucchetti
          && String(row.ragione_sociale || "").trim() !== String(principaleZucchetti).trim()
        ? String(row.ragione_sociale || "").trim()
        : null;
    const descrizione = rimuoviCodiceFinale(descrizioneBase, codiceDescrizione);
    const periodo = row.tipologia_fatturazione === "B"
      && ["payroll_bpoint", "altri_adempimenti", "forfait_consulenza", "forfait_f24", "import_file"].includes(row.sezione)
      ? "trimestrale" : String(row.periodicita_fatturazione || "mensile").toLowerCase();
    const baseKey = [soggetto, row.sezione, codicePrestazione].join("|");
    const occurrence = occurrences.get(baseKey) || 0;
    occurrences.set(baseKey, occurrence + 1);
    // Gli ID database cambiano quando il mese viene riaperto e rigenerato.
    // L'occorrenza mantiene invece stabile l'identità funzionale della voce;
    // importi e descrizioni restano fuori dalla chiave e vengono confrontati
    // nello snapshot per determinare se la voce è stata modificata.
    const key = `${baseKey}|${isRigaSezioneTreUnificabile(row) ? "" : occurrence}`;
    const current = groups.get(key) || {
      id: key,
      tipo: iipay ? "II" : row.tipologia_fatturazione,
      gruppo_fatturazione: iipay?.chiaveGruppo || null,
      codice_parcellazione: iipay?.chiaveGruppo || row.codice_parcellazione || "—",
      cliente: "",
      clienti: new Set(),
      aziendeGruppo: new Set(),
      codiciSocieta: new Set(),
      sezione: sezioneNumero(row.sezione),
      codice_prestazione: codicePrestazione,
      descrizioni: [],
      descrizioniAgo: [],
      codiciDescrizione: new Set(),
      periodi: new Set(),
      mesiCreazione: new Set(),
      mesiDestinazione: new Set(),
      prezziUnitari: new Set(),
      quantita: 0,
      importo_unitario: Number(row.importo_unitario || 0),
      importo_totale: 0,
      sconto: Number(row.sconto_percentuale_attivo || 0),
      cliente_diretto_iipay: Boolean(row.cliente_diretto_iipay),
      iipay_sconto_percentuale: Number(row.iipay_sconto_percentuale || 0),
    };
    current.clienti.add(principaleZucchetti || row.ragione_sociale);
    const ripeteSoloDescrizioneAgo = row.sezione === "altri_adempimenti"
      && !String(row.descrizione_extra_fattura || "").trim()
      && current.descrizioni.includes(descrizione);
    if (descrizione && !ripeteSoloDescrizioneAgo) current.descrizioni.push(descrizione);
    const descrizioneAgo = descrizioneFattura({ ...row, descrizione_extra_fattura: "" }, mostraCompetenzaAutomatica);
    if (descrizioneAgo) current.descrizioniAgo.push(rimuoviCodiceFinale(descrizioneAgo, codiceDescrizione));
    if (codiceDescrizione) current.codiciDescrizione.add(codiceDescrizione);
    current.periodi.add(periodo.charAt(0).toUpperCase() + periodo.slice(1));
    // Il mese di creazione è quello del periodo in cui la riga è nata, ricavato
    // dall'offset di fatturazione: mese_competenza non va bene perché i forfait
    // (1201/1203/1204/1429) lo valorizzano con la competenza cedolini, cioè il
    // mese precedente alla generazione, e uscirebbero sempre indietro di uno.
    const offsetRiga = Number(row.fatturazione_offset || 0);
    if (offsetRiga > 0 && periodoSelezionato) {
      const origine = new Date(periodoSelezionato.anno, periodoSelezionato.mese - 1 - offsetRiga, 1);
      current.mesiCreazione.add(origine.getFullYear() * 12 + origine.getMonth());
    }
    if (offsetRiga === 0 && periodoSelezionato) {
      const destinazione = destinazioneFatturazione(row, periodoSelezionato.anno, periodoSelezionato.mese);
      if (destinazione) current.mesiDestinazione.add(destinazione);
    }
    if (Number(row.importo_unitario || 0)) current.prezziUnitari.add(Number(row.importo_unitario));
    current.aziendeGruppo.add(row.codice_univoco_azienda);
    if (iipay) current.codiciSocieta.add(iipay.completo);
    current.quantita += Number(row.quantita || 0);
    current.importo_totale += Number(row.totale || 0);
    current.sconto = Math.max(current.sconto, Number(row.sconto_percentuale_attivo || 0));
    current.cliente_diretto_iipay = current.cliente_diretto_iipay || Boolean(row.cliente_diretto_iipay);
    current.iipay_sconto_percentuale = Math.max(current.iipay_sconto_percentuale, Number(row.iipay_sconto_percentuale || 0));
    groups.set(key, current);
  });
  return [...groups.values()].map((row) => {
    const prezzi = [...row.prezziUnitari];
    return {
      ...row,
      cliente: [...row.clienti].sort().join("; "),
      aziende_gruppo: [...row.aziendeGruppo],
      codice_parcellazione: row.codiciSocieta.size === 1 ? [...row.codiciSocieta][0] : row.codice_parcellazione,
      descrizione: [aggregaDescrizioni(row.descrizioni), ...row.codiciDescrizione].filter(Boolean).join(" - "),
      descrizione_ago: [aggregaDescrizioni(row.descrizioniAgo), ...row.codiciDescrizione].filter(Boolean).join(" - "),
      periodo: [...row.periodi].join("; "),
      mese_creazione: [...row.mesiCreazione].sort((a, b) => a - b)
        .map((valore) => `${MESI_LABEL[valore % 12]} ${Math.floor(valore / 12)}`)
        .join("; "),
      mese_destinazione: [...row.mesiDestinazione].join("; "),
      quantita: mostraCompetenzaAutomatica && row.sezione === 0 ? null : row.quantita,
      importo_unitario: mostraCompetenzaAutomatica && row.sezione === 0 ? null : prezzi.length === 1 ? prezzi[0] : (row.quantita ? row.importo_totale / row.quantita : 0),
      clienti: undefined, aziendeGruppo: undefined, codiciSocieta: undefined,
      descrizioni: undefined, descrizioniAgo: undefined, codiciDescrizione: undefined, periodi: undefined, mesiCreazione: undefined, mesiDestinazione: undefined, prezziUnitari: undefined,
    };
  }).sort((a, b) => a.cliente.localeCompare(b.cliente, "it") || a.codice_prestazione.localeCompare(b.codice_prestazione, "it", { numeric: true }));
};

export const creaFoglioFatturazione = (rows, labelTotale, vista = "competenza", colonneEscluse = []) => {
  const mostraProvenienza = vista === "fattura";
  const mostraDestinazione = vista === "competenza";
  let headers = ["Tipo", "Cod. parcellazione", "Cliente", "Sezione", "Cod. prestazione", "Descrizione"];
  if (mostraDestinazione) headers.push("Spostata a");
  if (mostraProvenienza) headers.push("Provenienza");
  headers.push("Periodo", "Quantità", "Importo unitario", "Importo totale", "Sconto %");
  let data = rows.map((row) => [
    row.tipo === "II" ? "I" : row.tipo, row.codice_parcellazione, row.cliente, row.sezione,
    row.codice_prestazione, row.descrizione,
    ...(mostraDestinazione ? [row.mese_destinazione || ""] : []),
    ...(mostraProvenienza ? [row.mese_creazione || ""] : []),
    row.periodo, row.quantita == null ? "" : Number(row.quantita || 0),
    row.importo_unitario == null ? "" : Number(row.importo_unitario || 0), Number(row.importo_totale || 0), Number(row.sconto || 0),
  ]);
  const larghezze = [8, 20, 38, 10, 18, 65, ...((mostraDestinazione || mostraProvenienza) ? [18] : []), 20, 12, 18, 18, 12];
  const indiciVisibili = headers.map((_, index) => index).filter((index) => !colonneEscluse.includes(headers[index]));
  headers = indiciVisibili.map((index) => headers[index]);
  data = data.map((row) => indiciVisibili.map((index) => row[index]));
  const indiceTotale = headers.indexOf("Importo totale");
  const totale = rows.reduce((sum, row) => sum + Number(row.importo_totale || 0), 0);
  const totaleRiga = Array(headers.length).fill("");
  totaleRiga[0] = labelTotale;
  totaleRiga[indiceTotale] = totale;
  const ws = XLSX.utils.aoa_to_sheet([headers, ...data, totaleRiga]);
  ws["!cols"] = indiciVisibili.map((index) => ({ wch: larghezze[index] }));
  ws["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${data.length + 1}` };
  ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft", state: "frozen" };
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let c = range.s.c; c <= range.e.c; c++) {
    ws[XLSX.utils.encode_cell({ r: 0, c })].s = {
      fill: { fgColor: { rgb: "2563EB" } }, font: { bold: true, color: { rgb: "FFFFFF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
    };
  }
  for (let r = 1; r <= data.length; r++) {
    for (const [header, formato] of [
      ["Importo unitario", '#,##0.00 [$€-it-IT]'], ["Importo totale", '#,##0.00 [$€-it-IT]'],
      ["Quantità", "#,##0.00"], ["Sconto %", "0.00"],
    ]) {
      const c = headers.indexOf(header);
      if (c < 0) continue;
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell) cell.z = formato;
    }
  }
  const totalRow = data.length + 1;
  for (let c = 0; c < headers.length; c++) {
    const cell = ws[XLSX.utils.encode_cell({ r: totalRow, c })];
    if (cell) cell.s = { fill: { fgColor: { rgb: "DBEAFE" } }, font: { bold: true, color: { rgb: "1E3A8A" } } };
  }
  ws[XLSX.utils.encode_cell({ r: totalRow, c: indiceTotale })].z = '#,##0.00 [$€-it-IT]';
  return ws;
};

const isRigaSezioneTreCalvi = (row) => row.tipo === "Z"
  && String(row.codice_parcellazione).trim() === "34828"
  && row.sezione === 3;

export const rigaPerCsvAgo = (row, preferences) => isRigaSezioneTreCalvi(row) && preferences[row.id]
  ? { ...row, descrizione: row.descrizione_ago || "" }
  : row;

const righeSezioneTreCalviDaFatturare = (lines, anno, mese, preferences = {}) =>
  aggregaRighe(lines.filter((row) => rigaNellaFattura(row, mese)), { anno, mese }, true)
    .filter((row) => isRigaSezioneTreCalvi(row) && preferences[row.id]);

const codicePrestazioneCollator = new Intl.Collator("it", {
  numeric: true,
  sensitivity: "base",
});

const confrontaCodiciPrestazione = (a, b) =>
  codicePrestazioneCollator.compare(String(a ?? "").trim(), String(b ?? "").trim());

const agoSnapshot = (row = {}) => ({
  codice_parcellazione: String(row.codice_parcellazione || ""), cliente: String(row.cliente || ""),
  tipo: String(row.tipo || ""), sezione: String(row.sezione || ""),
  codice_prestazione: String(row.codice_prestazione || ""), descrizione: String(row.descrizione || ""),
  periodo: String(row.periodo || ""), quantita: Number(row.quantita || 0),
  importo_unitario: Number(row.importo_unitario || 0), importo_totale: Number(row.importo_totale || 0),
  sconto: Number(row.sconto || 0),
});
const isAgoRowChanged = (row, previous) => JSON.stringify(agoSnapshot(row)) !== JSON.stringify(previous || {});
const statoAgo = (row, latest, calviDescriptionPreferences) => {
  const previous = latest?.[row.id];
  if (!previous) return "Da esportare";
  return isAgoRowChanged(rigaPerCsvAgo(row, calviDescriptionPreferences), previous.snapshot)
    ? "Modificata" : "Esportata";
};
const bytesToBase64 = (bytes) => {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  }
  return window.btoa(binary);
};

export const tipoFatturazioneVisibile = (row, tipologie) =>
  tipologie.size === 0 || tipologie.has(row.tipo === "II" ? "I" : row.tipo);

export const tipologieFatturazioneSalvate = (raw) => {
  try {
    const saved = JSON.parse(raw);
    const valid = Array.isArray(saved) ? [...new Set(saved
      .map((tipo) => tipo === "II" ? "I" : tipo)
      .filter((tipo) => ["Z", "I", "B"].includes(tipo)))] : [];
    return Array.isArray(saved) && saved.length === 0 ? [] : valid.length ? valid : ["Z", "I", "B"];
  } catch { return ["Z", "I", "B"]; }
};

export const Fatturazione = ({ data, onClosureChange, closureTarget }) => {
  const isAdmin = knowAcces(['parc_admin']);
  const [bpointClosure, setBpointClosure] = useState(null);
  const [closingBpoint, setClosingBpoint] = useState(false);
  const [closingZI, setClosingZI] = useState(false);
  const [lines, setLines] = useState([]);
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [periodoId, setPeriodoId] = useState(null);
  const [periodoChiuso, setPeriodoChiuso] = useState(false);
  const [agoData, setAgoData] = useState({ latest: {}, exports: [] });
  const [agoHistoryOpen, setAgoHistoryOpen] = useState(false);
  const [exportingAgo, setExportingAgo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportingCalvi, setExportingCalvi] = useState(false);
  const [calviDescriptionPreferences, setCalviDescriptionPreferences] = useState({});
  const [savingCalviRow, setSavingCalviRow] = useState(null);
  const [righeSelezionate, setRigheSelezionate] = useState([]);
  const [filtroStatoAgo, setFiltroStatoAgo] = useState("");
  const [cliente, setCliente] = useState("");
  const [totaliOpen, setTotaliOpen] = useState(false);
  const [sezioneEsplosa, setSezioneEsplosa] = useState(null);
  const [mostraSconti, setMostraSconti] = useState(false);
  const [tipologie, setTipologie] = useState(new Set());
  const [vista, setVista] = useState(() =>
    localStorage.getItem(VISTA_FATTURAZIONE_KEY) === "fattura" ? "fattura" : "competenza"
  );
  const [anno, mese] = data.split("-").map(Number);
  const rows = useMemo(() => aggregaRighe(
    vista === "fattura"
      ? invoiceLines.filter((row) => rigaNellaFattura(row, mese))
      : lines,
    { anno, mese },
    vista === "fattura",
    mostraSconti
  ), [lines, invoiceLines, vista, anno, mese, mostraSconti]);

  const load = useCallback(async (isActive) => {
    setPeriodoId(null);
    setInvoiceLines([]);
    setLoading(true);
    try {
      const period = await api.get(`/billing/periods/${anno}/${mese}`);
      if (!isActive()) return;
      const result = await api.get(`/billing/lines/${period.data.id}`);
      if (!isActive()) return;
      setLines(result.data || []);
      setPeriodoChiuso(period.data.stato === "chiuso");
      // La lettura delle righe genera il Payroll: avviare invoice-view solo
      // dopo che ha terminato, altrimenti la sezione 0 puo' risultare vuota.
      setPeriodoId(period.data.id);
    } catch (error) {
      if (isActive()) toast.error(error.response?.data?.message || "Errore caricamento fatturazione");
    } finally { if (isActive()) setLoading(false); }
  }, [anno, mese]);

  useEffect(() => {
    let active = true;
    setPeriodoId(null);
    setLines([]);
    setInvoiceLines([]);
    setLoading(true);
    const timer = setTimeout(() => load(() => active), 300);
    return () => { active = false; clearTimeout(timer); };
  }, [load]);
  useEffect(() => {
    setBpointClosure(null);
    if (!isAdmin || vista !== "fattura" || !periodoId || ![1, 4, 7, 10].includes(mese) || loading) return;
    let active = true;
    api.get(`/billing/bpoint-invoice/${periodoId}/status`)
      .then(result => { if (active) setBpointClosure(result.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [isAdmin, vista, periodoId, mese, loading]);

  const toggleZI = async () => {
    if (!window.confirm(`${periodoChiuso ? 'Riaprire' : 'Chiudere'} tutto Zucchetti/iiPay del mese selezionato?`)) return;
    setClosingZI(true);
    try {
      const result = await api.post(`/billing/periods/${periodoId}/${periodoChiuso ? 'reopen' : 'close-all'}`);
      setPeriodoChiuso(result.data.period.stato === 'chiuso');
      const saved = await api.get(`/billing/lines/${periodoId}/invoice-view`);
      setInvoiceLines(saved.data || []);
      onClosureChange?.();
      toast.success(periodoChiuso ? 'Zucchetti/iiPay riaperti' : 'Zucchetti/iiPay chiusi');
    } catch (error) { toast.error(error.response?.data?.message || 'Errore chiusura Zucchetti/iiPay'); }
    finally { setClosingZI(false); }
  };

  const closeBpointInvoice = async ({ reopen = false, excludeCurrentAdempimenti = false } = {}) => {
    const periodoLabel = `${String(mese).padStart(2, '0')}/${anno}`;
    const message = reopen
      ? `Riaprire il trimestre BPoint e le eventuali chiusure degli altri adempimenti di ${periodoLabel}?`
      : excludeCurrentAdempimenti
        ? `Chiudere il trimestre BPoint da fatturare in ${periodoLabel}, lasciando aperti gli altri adempimenti di ${periodoLabel} per il trimestre successivo?`
        : `Chiudere tutto BPoint, inclusi gli altri adempimenti di ${periodoLabel}? Il trimestre già chiuso resta invariato.`;
    if (!window.confirm(message)) return;
    setClosingBpoint(true);
    try {
      const result = await api.post(`/billing/bpoint-invoice/${periodoId}/${reopen ? 'reopen' : 'close'}`, {
        exclude_current_adempimenti: excludeCurrentAdempimenti,
      });
      setBpointClosure(result.data);
      const saved = await api.get(`/billing/lines/${periodoId}/invoice-view`);
      setInvoiceLines(saved.data || []);
      onClosureChange?.();
      toast.success(reopen ? 'Trimestre BPoint riaperto' : excludeCurrentAdempimenti
        ? 'Trimestre BPoint chiuso; altri adempimenti del mese ancora aperti' : 'BPoint chiuso, inclusi gli altri adempimenti del mese');
    } catch (error) { toast.error(error.response?.data?.message || 'Errore chiusura trimestre BPoint'); }
    finally { setClosingBpoint(false); }
  };

  useEffect(() => {
    if (vista !== "fattura" || !periodoId) return;
    let active = true;
    setLoading(true);
    api.get(`/billing/lines/${periodoId}/invoice-view`)
      .then((result) => { if (active) setInvoiceLines(result.data || []); })
      .catch((error) => {
        if (active) toast.error(error.response?.data?.message || "Errore caricamento fattura del mese");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [vista, periodoId]);
  useEffect(() => { localStorage.setItem(VISTA_FATTURAZIONE_KEY, vista); }, [vista]);
  useEffect(() => { setRigheSelezionate([]); }, [vista, anno, mese]);
  const loadAgoHistory = useCallback(async () => {
    if (!periodoId) return;
    try {
      const result = await api.get(`/billing/ago-exports/periodo/${periodoId}`);
      setAgoData(result.data || { latest: {}, exports: [] });
      setPeriodoChiuso(Boolean(result.data?.periodo_chiuso));
    } catch (error) {
      if (error.response?.status !== 403) toast.error(error.response?.data?.message || "Errore caricamento storico AGO");
    }
  }, [periodoId]);
  useEffect(() => { if (vista === "fattura") loadAgoHistory(); }, [vista, loadAgoHistory]);
  useEffect(() => {
    if (vista !== "fattura" || !periodoId) return;
    api.get(`/billing/calvi-description-preferences/${periodoId}`)
      .then((result) => setCalviDescriptionPreferences(result.data || {}))
      .catch((error) => toast.error(error.response?.data?.message || "Errore caricamento preferenze Calvi"));
  }, [vista, periodoId]);

  const toggleCalviDescription = useCallback(async (row) => {
    if (!periodoId || savingCalviRow) return;
    const hidden = !calviDescriptionPreferences[row.id];
    setSavingCalviRow(row.id);
    try {
      await api.put(`/billing/calvi-description-preferences/${periodoId}`, {
        row_key: row.id,
        nascondi_descrizione_obbligatoria: hidden,
      });
      setCalviDescriptionPreferences((current) => {
        const next = { ...current };
        if (hidden) next[row.id] = true;
        else delete next[row.id];
        return next;
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore salvataggio preferenza Calvi");
    } finally { setSavingCalviRow(null); }
  }, [periodoId, savingCalviRow, calviDescriptionPreferences]);

  const rowsTipologia = useMemo(() => rows.filter((row) => tipoFatturazioneVisibile(row, tipologie)), [rows, tipologie]);
  const clienti = useMemo(() => [...new Set(rowsTipologia.flatMap((row) => row.cliente.split("; ")))].sort((a, b) => a.localeCompare(b, "it")), [rowsTipologia]);
  const clienteTipi = useMemo(() => {
    const result = new Map();
    rowsTipologia.forEach((row) => row.cliente.split("; ").forEach((nome) => result.set(nome, row.tipo === "II" ? "I" : row.tipo)));
    return result;
  }, [rowsTipologia]);
  const gruppi = useMemo(() => {
    const result = new Map();
    rowsTipologia.forEach((row) => {
      const gruppo = getGruppoRiga(row);
      if (!gruppo) return;
      const current = result.get(gruppo.id) || { ...gruppo, aziende: new Set() };
      (row.aziende_gruppo || []).forEach((codice) => current.aziende.add(codice));
      result.set(gruppo.id, current);
    });
    return [...result.values()].filter((gruppo) => gruppo.aziende.size > 1)
      .sort((a, b) => a.label.localeCompare(b.label, "it"));
  }, [rowsTipologia]);
  const clienteOptions = useMemo(() => [
    ...gruppi.map((gruppo) => ({
      id: `gruppo:${gruppo.id}`,
      name: `${gruppo.label} — Gruppo di fatturazione`,
      badge: { label: `Gruppo ${gruppo.tipo}`, className: gruppo.tipo === "I" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700" },
    })),
    ...clienti.map((item) => ({ id: `cliente:${item}`, name: item, badge: clienteTipi.get(item) })),
  ], [clienti, gruppi, clienteTipi]);
  const clienteSelectWidth = useMemo(() => Math.min(680, Math.max(320,
    clienteOptions.reduce((max, option) => Math.max(max, option.name.length * 8 + 110), 0)
  )), [clienteOptions]);
  useEffect(() => {
    if (cliente && !clienteOptions.some((option) => option.id === cliente)) setCliente("");
  }, [cliente, clienteOptions]);
  const rowsCliente = useMemo(() => {
    if (!cliente) return rowsTipologia;
    if (cliente.startsWith("gruppo:")) {
      const gruppoId = cliente.slice("gruppo:".length);
      return rowsTipologia.filter((row) => getGruppoRiga(row)?.id === gruppoId);
    }
    const nomeCliente = cliente.slice("cliente:".length);
    return rowsTipologia.filter((row) => row.cliente.split("; ").includes(nomeCliente));
  }, [rowsTipologia, cliente]);
  const visibleRows = useMemo(() => vista === "fattura" && filtroStatoAgo
    ? rowsCliente.filter((row) => statoAgo(row, agoData.latest, calviDescriptionPreferences) === filtroStatoAgo)
    : rowsCliente,
  [vista, filtroStatoAgo, rowsCliente, agoData.latest, calviDescriptionPreferences]);
  const totale = useMemo(() => visibleRows.reduce((sum, row) => sum + row.importo_totale, 0), [visibleRows]);
  const isRigaScontoTecnica = useCallback((row) => row.sezione === 7 || (
    ["I", "II"].includes(String(row.tipo || "").toUpperCase())
      && row.codice_prestazione === "—"
      && Number(row.importo_totale || 0) < 0
      && /sconto|discount/i.test(String(row.descrizione || ""))
  ), []);
  const selectedAgoRows = (gestionale) => visibleRows.filter(row => righeSelezionate.includes(row.id)
    && (gestionale === 'bpoint' ? row.tipo === 'B' : ['Z', 'I', 'II'].includes(row.tipo)) && !isRigaScontoTecnica(row));
  const gestionaleChiuso = (row) => row.tipo === 'B' ? Boolean(bpointClosure?.closed) : periodoChiuso;
  const agoClosureReady = (gestionale) => selectedAgoRows(gestionale).length > 0
    && (gestionale === 'bpoint' ? bpointClosure?.closed : periodoChiuso);
  const hasBpointInvoiceRows = rows.some(row => row.tipo === 'B' && !isRigaScontoTecnica(row));
  const exportCsvAgo = async (gestionale) => {
    if (!agoClosureReady(gestionale)) {
      toast.info("Chiudi la fatturazione dei gestionali selezionati prima di esportare il CSV AGO");
      return;
    }
    const selected = new Set(selectedAgoRows(gestionale).map(row => row.id));
    const rowsToExport = visibleRows.map((row) => rigaPerCsvAgo(row, calviDescriptionPreferences))
      .filter((row) => selected.has(row.id) && !isRigaScontoTecnica(row)
        && (!agoData.latest?.[row.id] || isAgoRowChanged(row, agoData.latest[row.id].snapshot)));
    if (!rowsToExport.length) {
      toast.info("Seleziona almeno una riga da esportare");
      return;
    }
    try {
      setExportingAgo(gestionale);
      const csv = creaCsvValorizzazioniAgo(rowsToExport);
      const encodedCsv = codificaWindows1252(csv);
      const result = await api.post(`/billing/ago-exports/periodo/${periodoId}`, { gestionale, rows: rowsToExport, csv_base64: bytesToBase64(encodedCsv) });
      const blob = new Blob([encodedCsv], { type: "text/csv;charset=windows-1252" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = result.data.nome_file;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(link.href);
      setRigheSelezionate(current => current.filter(id => !rowsToExport.some(row => row.id === id)));
      await loadAgoHistory();
      toast.success(`Esportazione AGO #${result.data.numero} registrata`);
    } catch (error) { toast.error(error.response?.data?.message || "Errore esportazione AGO"); }
    finally { setExportingAgo(false); }
  };
  const exportExcel = useCallback(async () => {
    if (!periodoId) return;
    try {
      let storicoFattura = invoiceLines;
      if (!storicoFattura.length) {
        const result = await api.get(`/billing/lines/${periodoId}/invoice-view`);
        storicoFattura = result.data || [];
        setInvoiceLines(storicoFattura);
      }

      const applicaFiltri = (source, mostraCompetenzaAutomatica = false, tipiDedicati = null) => {
        let result = aggregaRighe(source, { anno, mese }, mostraCompetenzaAutomatica, mostraSconti).filter((row) =>
          tipiDedicati ? tipiDedicati.includes(row.tipo) : tipoFatturazioneVisibile(row, tipologie)
        );
        if (!cliente) return result;
        if (cliente.startsWith("gruppo:")) {
          const gruppoId = cliente.slice("gruppo:".length);
          return result.filter((row) => getGruppoRiga(row)?.id === gruppoId);
        }
        const nomeCliente = cliente.slice("cliente:".length);
        return result.filter((row) => row.cliente.split("; ").includes(nomeCliente));
      };

      const competenza = applicaFiltri(lines);
      const daFatturare = applicaFiltri(
        storicoFattura.filter((row) => rigaNellaFattura(row, mese)),
        true
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, creaFoglioFatturazione(competenza, "TOTALE COMPETENZA", "competenza"), "Competenza");
      XLSX.utils.book_append_sheet(wb, creaFoglioFatturazione(daFatturare, "TOTALE DA FATTURARE", "fattura"), "Da fatturare");
      XLSX.utils.book_append_sheet(wb, creaFoglioFatturazione(applicaFiltri(lines, false, ["B"]), "TOTALE COMPETENZA BPOINT", "competenza"), "BPoint Competenza");
      XLSX.utils.book_append_sheet(wb, creaFoglioFatturazione(applicaFiltri(storicoFattura.filter((row) => rigaNellaFattura(row, mese)), true, ["B"]), "TOTALE DA FATTURARE BPOINT", "fattura"), "BPoint Da fatturare");
      XLSX.utils.book_append_sheet(wb, creaFoglioFatturazione(applicaFiltri(lines, false, ["Z", "I", "II"]), "TOTALE COMPETENZA ZUCCHETTI/IIPAY", "competenza"), "Zucchetti iiPay Competenza");
      XLSX.utils.book_append_sheet(wb, creaFoglioFatturazione(applicaFiltri(storicoFattura.filter((row) => rigaNellaFattura(row, mese)), true, ["Z", "I", "II"]), "TOTALE DA FATTURARE ZUCCHETTI/IIPAY", "fattura"), "Zucchetti iiPay Da fatturare");
      XLSX.writeFile(wb, `fatturazione_${anno}_${String(mese).padStart(2, "0")}.xlsx`, { cellStyles: true });
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore esportazione fatturazione");
    }
  }, [periodoId, invoiceLines, tipologie, cliente, lines, mese, anno, mostraSconti]);
  const exportSezioneTreCalvi = useCallback(async () => {
    if (vista !== "fattura" || !periodoId || loading || exportingCalvi) return;
    setExportingCalvi(true);
    try {
      const result = await api.get(`/billing/lines/${periodoId}/invoice-view`);
      const righe = righeSezioneTreCalviDaFatturare(result.data || [], anno, mese, calviDescriptionPreferences);
      if (!righe.length) {
        toast.info("Nessuna riga CALVI/SIPA/RISA marcata per nascondere la descrizione obbligatoria");
        return;
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb,
        creaFoglioFatturazione(righe, "TOTALE SEZ. 3 CALVI", "fattura",
          ["Sconto %", "Sezione", "Provenienza", "Periodo"]), "Sez. 3 Calvi");
      XLSX.writeFile(wb, `fatturazione_calvi_sez3_${anno}_${String(mese).padStart(2, "0")}.xlsx`, { cellStyles: true });
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore esportazione sezione 3 Calvi");
    } finally {
      setExportingCalvi(false);
    }
  }, [vista, periodoId, loading, exportingCalvi, anno, mese, calviDescriptionPreferences]);
  const totaliPerSezione = useMemo(() => {
    const result = new Map();
    visibleRows.forEach((row) => {
      const sezione = row.sezione;
      const current = result.get(sezione) || {
        sezione,
        descrizione: SEZIONI_LABEL[sezione] || `Sezione ${sezione}`,
        tipologie: new Set(),
        quantita: 0,
        totale: 0,
      };
      current.tipologie.add(row.tipo === "II" ? "I" : row.tipo);
      current.quantita += Number(row.quantita || 0);
      current.totale += Number(row.importo_totale || 0);
      result.set(sezione, current);
    });
    return [...result.values()].sort((a, b) => Number(a.sezione) - Number(b.sezione));
  }, [visibleRows]);
  const totaliPerVoce = useMemo(() => {
    const result = new Map();
    visibleRows.forEach((row) => {
      const current = result.get(row.codice_prestazione) || {
        codice: row.codice_prestazione,
        descrizioni: new Set(),
        tipologie: new Set(),
        quantita: 0,
        totale: 0,
      };
      if (row.descrizione) current.descrizioni.add(row.descrizione);
      current.tipologie.add(row.tipo === "II" ? "I" : row.tipo);
      current.quantita += Number(row.quantita || 0);
      current.totale += Number(row.importo_totale || 0);
      result.set(row.codice_prestazione, current);
    });
    return [...result.values()].map((row) => ({
      ...row,
      descrizione: [...row.descrizioni].join("; "),
    })).sort((a, b) => String(a.codice).localeCompare(String(b.codice), "it", { numeric: true }));
  }, [visibleRows]);
  const totaliPerVoceSezione = useMemo(() => sezioneEsplosa == null
    ? []
    : totaliPerVoce.filter((voce) => visibleRows.some((row) =>
      row.sezione === sezioneEsplosa && row.codice_prestazione === voce.codice
    )).map((voce) => {
      const righe = visibleRows.filter((row) =>
        row.sezione === sezioneEsplosa && row.codice_prestazione === voce.codice
      );
      return {
        ...voce,
        descrizione: [...new Set(righe.map((row) => row.descrizione).filter(Boolean))].join("; "),
        tipologie: new Set(righe.map((row) => row.tipo === "II" ? "I" : row.tipo)),
        quantita: righe.reduce((sum, row) => sum + Number(row.quantita || 0), 0),
        totale: righe.reduce((sum, row) => sum + Number(row.importo_totale || 0), 0),
      };
    }), [sezioneEsplosa, totaliPerVoce, visibleRows]);
  const totaleModale = sezioneEsplosa == null
    ? totale
    : totaliPerSezione.find((row) => row.sezione === sezioneEsplosa)?.totale || 0;

  const [annullingAgo, setAnnullingAgo] = useState(null);
  const annulAgoExport = async (item) => {
    if (!window.confirm(`Annullare l'export #${item.numero}? Le voci torneranno esportabili. Le valorizzazioni già importate in AGO devono essere gestite in AGO prima del nuovo import.`)) return;
    try {
      setAnnullingAgo(item.id);
      await api.post(`/billing/ago-exports/${item.id}/annulla`);
      setRigheSelezionate([]);
      await loadAgoHistory();
      toast.success(`Export #${item.numero} annullato: le voci sono nuovamente esportabili`);
    } catch (error) { toast.error(error.response?.data?.message || "Errore annullamento esportazione"); }
    finally { setAnnullingAgo(null); }
  };

  const downloadAgoExport = async (item) => {
    try {
      const result = await api.get(`/billing/ago-exports/${item.id}/download`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(result.data); link.download = item.nome_file;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(link.href);
    } catch (error) { toast.error(error.response?.data?.message || "Errore download esportazione"); }
  };

  const columns = [
    { field: "tipo", headerName: "Tipo", width: 75, renderCell: ({ value }) => (
      <span className={`rounded px-2 py-0.5 text-xs font-bold ${
        value === "Z"
          ? "bg-blue-100 text-blue-700"
          : value === "B"
            ? "bg-purple-100 text-purple-700"
            : "bg-green-100 text-green-700"
      }`}>
        {value}
      </span>
    ) },
    { field: "codice_parcellazione", headerName: "Cod. parc.", width: 120 },
    { field: "cliente", headerName: "Cliente", minWidth: 260, flex: 1 },
    { field: "sezione", headerName: "Sezione", type: "number", width: 90 },
    {
      field: "codice_prestazione",
      headerName: "Cod. prestazione",
      width: 135,
      sortComparator: confrontaCodiciPrestazione,
    },
    ...(vista === "fattura" ? [{
      field: "stato_ago", headerName: "Stato AGO", width: 145, sortable: false, filterable: false,
      renderCell: ({ row }) => {
        const previous = agoData.latest?.[row.id];
        const stato = statoAgo(row, agoData.latest, calviDescriptionPreferences);
        const label = stato === "Esportata" && previous?.export_count > 1 ? `Riesportata ×${previous.export_count}` : stato;
        const style = stato === "Da esportare" ? "bg-blue-100 text-blue-700" : stato === "Modificata" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700";
        return <span className={`rounded-full px-2 py-1 text-xs font-semibold ${style}`}>{label}</span>;
      },
    }] : []),
    { field: "descrizione", headerName: "Descrizione AGO + obbligatoria", minWidth: 320, flex: 1.2 },
    ...(vista === "fattura" ? [{
      field: "azione_descrizione_calvi", headerName: "Descrizione CALVI", width: 205, sortable: false, filterable: false,
      renderCell: ({ row }) => isRigaSezioneTreCalvi(row) ? (
        <button type="button" disabled={savingCalviRow === row.id} onClick={() => toggleCalviDescription(row)}
          className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition disabled:opacity-50 ${calviDescriptionPreferences[row.id]
            ? "border-amber-300 bg-amber-50 text-amber-800"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
          {savingCalviRow === row.id ? "Salvataggio…" : calviDescriptionPreferences[row.id] ? "Ripristina obbligatoria" : "Nascondi obbligatoria"}
        </button>
      ) : null,
    }] : []),
    ...(vista === "competenza" ? [{
      field: "mese_destinazione",
      headerName: "Spostata a",
      width: 135,
      description: "Anteprima del mese in cui la voce confluirà; vuoto se resta nel mese selezionato",
      renderCell: ({ value }) => value ? (
        <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{value}</span>
      ) : "—",
    }] : []),
    ...(vista === "fattura" ? [{
      field: "mese_creazione",
      headerName: "Provenienza",
      width: 145,
      description: "Mese e anno da cui arrivano le voci confluite nella fattura corrente",
    }] : []),
    { field: "periodo", headerName: "Periodo fatturazione", width: 145 },
    { field: "quantita", headerName: "Quantità", type: "number", width: 105, valueFormatter: ({ value }) => value == null ? "" : Number(value) === 0 ? "—" : quantita(value) },
    { field: "importo_unitario", headerName: "Importo unitario", type: "number", width: 140, valueFormatter: ({ value }) => value == null ? "" : Number(value) === 0 ? "—" : euro(value) },
    { field: "importo_totale", headerName: "Importo totale", type: "number", width: 140, valueFormatter: ({ value }) => Number(value) === 0 ? "—" : euro(value) },
    { field: "sconto", headerName: "Sconto", type: "number", width: 95, valueFormatter: ({ value }) => Number(value) === 0 ? "—" : `${formatItalianNumber(value, { maximumFractionDigits: 2 })}%` },
  ];

  const closureButtons = <>
      {isAdmin && vista === "fattura" && [1, 4, 7, 10].includes(mese) && (bpointClosure?.eligible || bpointClosure?.closed) && !loading && (
        <>
          {!bpointClosure?.closed && <button type="button"
            title="Chiudi tutto BPoint tranne sez. 3" aria-label="Chiudi tutto BPoint tranne sez. 3"
            onClick={() => closeBpointInvoice({ excludeCurrentAdempimenti: true })}
            disabled={closingBpoint || closingZI} className={closureButtonClass(false)}>
            {closingBpoint ? "Attendi..." : "Chiudi BPoint tranne sez. 3"}
          </button>}
          {!bpointClosure?.current_month_adempimenti_closed && <button type="button"
            title="Chiudi tutto BPoint" aria-label="Chiudi tutto BPoint"
            onClick={() => closeBpointInvoice()} disabled={closingBpoint || closingZI}
            className={closureButtonClass(false)}>
            {closingBpoint ? "Attendi..." : "Chiudi BPoint"}
          </button>}
          {bpointClosure?.closed && <button type="button"
            title="Riapri tutto BPoint" aria-label="Riapri tutto BPoint"
            onClick={() => closeBpointInvoice({ reopen: true })} disabled={closingBpoint || closingZI}
            className={closureButtonClass(true)}>
            {closingBpoint ? "Attendi..." : "Riapri BPoint"}
          </button>}
        </>
      )}
      {isAdmin && vista === "fattura" && <button type="button" onClick={toggleZI}
        title={`${periodoChiuso ? "Riapri" : "Chiudi"} tutto Zucchetti/iiPay`}
        aria-label={`${periodoChiuso ? "Riapri" : "Chiudi"} tutto Zucchetti/iiPay`}
        disabled={!periodoId || loading || closingZI || closingBpoint}
        className={closureButtonClass(periodoChiuso)}>
        {closingZI ? "Attendi..." : `${periodoChiuso ? "Riapri" : "Chiudi"} Zuc./iiPay`}
      </button>}
  </>;

  const Toolbar = () => <GridToolbarContainer className="mb-3 flex w-full flex-wrap items-center justify-between gap-3 px-3 pt-2 pb-2">
    <div className="flex min-w-0 flex-wrap items-center gap-3">
      <GridToolbarQuickFilter placeholder="Cerca..." />
      {vista === "fattura" && <select aria-label="Filtra Stato AGO" value={filtroStatoAgo}
        onChange={(event) => { setFiltroStatoAgo(event.target.value); setRigheSelezionate([]); }}
        className="rounded border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-700">
        <option value="">Tutti gli stati AGO</option>
        <option value="Da esportare">Da esportare</option>
        <option value="Modificata">Modificata</option>
        <option value="Esportata">Esportata</option>
      </select>}
      <div className="flex gap-2">
        {TIPI_FATTURAZIONE.map(([tipo, colors]) => (
          <button key={tipo} type="button" onClick={() => setTipologie((current) => { const next = new Set(current); next.has(tipo) ? next.delete(tipo) : next.add(tipo); return next; })}
            className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${tipologie.has(tipo) ? `${colors} ring-1 ring-current` : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}>
            {tipo}
          </button>
        ))}
      </div>
      <div style={{ width: clienteSelectWidth }}>
        <Autocomplete label="" placeholder="Cerca cliente..." value={cliente}
          onChange={(event) => setCliente(event.target.value)} options={clienteOptions} />
      </div>
    </div>
    <div className="flex w-full flex-wrap items-center gap-3">
      <div className="flex rounded-lg border border-slate-200 bg-slate-100 p-1" aria-label="Visuale fatturazione">
        {[["competenza", "Competenza"], ["fattura", "Da fatturare"]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setVista(id)}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              vista === id ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}>
            {label}
          </button>
        ))}
      </div>
      <button type="button" onClick={() => { setSezioneEsplosa(null); setTotaliOpen(true); }}
        className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100">
        Totali per voce
      </button>
      <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition ${mostraSconti ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-white text-slate-600"}`}>
        <input type="checkbox" checked={mostraSconti} onChange={(event) => setMostraSconti(event.target.checked)} className="h-4 w-4 accent-emerald-600" />
        <span>Mostra sconti</span>
      </label>
      <button type="button" onClick={exportExcel}
        className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100">
        Esporta Excel
      </button>
      {vista === "fattura" && ['zucchetti_iipay', ...(hasBpointInvoiceRows ? ['bpoint'] : [])].map(gestionale => <button key={gestionale} type="button" onClick={() => exportCsvAgo(gestionale)} disabled={!agoClosureReady(gestionale) || Boolean(exportingAgo)}
        title={!agoClosureReady(gestionale) ? "Seleziona le righe del gestionale e chiudi la sua fatturazione prima di esportare" : `Esporta ${selectedAgoRows(gestionale).length} righe selezionate`}
        className={`rounded-lg border px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${gestionale === 'bpoint' ? 'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100' : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
        {exportingAgo === gestionale ? "Esportazione…" : `Esporta CSV AGO ${gestionale === 'bpoint' ? 'BPoint' : 'Zucchetti/iiPay'}${selectedAgoRows(gestionale).length ? ` (${selectedAgoRows(gestionale).length})` : ''}`}
      </button>)}
      {vista === "fattura" && <button type="button" onClick={() => { loadAgoHistory(); setAgoHistoryOpen(true); }}
        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
        Storico AGO{agoData.exports.length ? ` (${agoData.exports.length})` : ""}
      </button>}
      {vista === "fattura" && <button type="button" onClick={exportSezioneTreCalvi}
        disabled={!periodoId || loading || exportingCalvi}
        title="Esporta solo le righe CALVI, RISA e SIPA per cui è stata nascosta la descrizione obbligatoria"
        className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50">
        {exportingCalvi ? "Esportazione…" : "Esporta Sez. 3 Calvi"}
      </button>}
      <div className="ml-auto whitespace-nowrap text-sm font-semibold text-blue-700">
        {vista === "fattura" ? "Totale da fatturare" : "Totale competenza"}: {euro(totale)}
      </div>
    </div>
  </GridToolbarContainer>;

  return <>
    {closureTarget ? createPortal(closureButtons, closureTarget) : <div className="flex flex-nowrap items-center [justify-content:safe_flex-end] gap-1 overflow-x-auto py-0.5">{closureButtons}</div>}
    <div className="el-container overflow-hidden rounded-xl border border-gray-200 bg-white p-0" style={{ height: "calc(100vh - 140px)", minHeight: 620 }}>
      <DataGridPro rows={visibleRows} columns={columns} loading={loading} density="compact" disableRowSelectionOnClick
        checkboxSelection rowSelectionModel={righeSelezionate} onRowSelectionModelChange={setRigheSelezionate}
        isRowSelectable={({ row }) => !isRigaScontoTecnica(row) && gestionaleChiuso(row)
          && (!agoData.latest?.[row.id] || isAgoRowChanged(rigaPerCsvAgo(row, calviDescriptionPreferences), agoData.latest[row.id].snapshot))}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        slots={{ toolbar: Toolbar, footer: CustomFooterPersonalized, loadingOverlay: LinearProgress }}
        slotProps={{ footer: { fileName: `fatturazione_${vista}_${anno}_${String(mese).padStart(2, "0")}` } }} />
    </div>
    <Modal isOpen={agoHistoryOpen} size="large" closeButton={() => setAgoHistoryOpen(false)}
      headerText={<div className="text-center"><div>Storico esportazioni AGO</div><div className="text-xs font-normal text-gray-500">{String(mese).padStart(2, "0")}/{anno}</div></div>}>
      <div className="max-h-[70vh] space-y-3 overflow-auto p-1">
        {!agoData.exports.length && <div className="rounded-lg bg-slate-50 p-6 text-center text-sm text-slate-500">Nessuna esportazione registrata per questo mese.</div>}
        {agoData.exports.map((item) => {
          const codes = new Map();
          (item.voci || []).forEach((line) => {
            const current = codes.get(line.codice) || { codice: line.codice, importo: 0, count: 0, reexport: 0 };
            current.importo += Number(line.importo || 0); current.count += 1; current.reexport += line.riesportazione ? 1 : 0;
            codes.set(line.codice, current);
          });
          return <div key={item.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex flex-wrap items-center gap-3 bg-slate-50 px-4 py-3">
              <span className="font-bold text-blue-700">Export #{item.numero}</span>
              {item.annullato_at && <span className="text-xs font-semibold text-amber-700">Annullato · {new Date(item.annullato_at).toLocaleString("it-IT")} · {item.annullato_by}</span>}
              <span className="text-sm text-slate-600">{new Date(item.created_at).toLocaleString("it-IT")}</span>
              <span className="text-sm text-slate-600">Utente: {item.created_by || "—"}</span>
              <span className="ml-auto text-sm font-semibold">{item.numero_voci} voci · {euro(item.importo_totale)}</span>
              {!item.annullato_at && <button type="button" disabled={annullingAgo != null} onClick={() => annulAgoExport(item)}
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50">
                {annullingAgo === item.id ? "Annullamento…" : "Annulla export"}
              </button>}
              <button type="button" onClick={() => downloadAgoExport(item)} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">Scarica CSV</button>
            </div>
            <table className="w-full text-sm"><thead><tr className="border-b text-xs uppercase text-slate-500"><th className="px-4 py-2 text-left">Codice</th><th className="px-4 py-2 text-right">Voci</th><th className="px-4 py-2 text-right">Riesportate</th><th className="px-4 py-2 text-right">Importo</th></tr></thead>
              <tbody>{[...codes.values()].map((line) => <tr key={line.codice} className="border-b last:border-0"><td className="px-4 py-2 font-semibold">{line.codice || "—"}</td><td className="px-4 py-2 text-right">{line.count}</td><td className="px-4 py-2 text-right">{line.reexport || "—"}</td><td className="px-4 py-2 text-right font-semibold">{euro(line.importo)}</td></tr>)}</tbody>
            </table>
          </div>;
        })}
      </div>
    </Modal>
    <Modal isOpen={totaliOpen} size="compact" closeButton={() => { setTotaliOpen(false); setSezioneEsplosa(null); }}
      headerText={<div className="text-center"><div>{sezioneEsplosa == null ? "Totali per sezione" : `Esploso ${SEZIONI_LABEL[sezioneEsplosa] || `sezione ${sezioneEsplosa}`}`}</div><div className="mt-0.5 text-xs font-normal text-gray-500">Fatturazione {String(mese).padStart(2, "0")}/{anno}</div></div>}>
      <div className="overflow-hidden rounded-md border border-slate-200">
        <div className="grid gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Mostra tipologia</div>
            <div className="flex gap-2">
              {TIPI_FATTURAZIONE.map(([tipo, colors]) => (
                <button key={tipo} type="button" onClick={() => setTipologie((current) => { const next = new Set(current); next.has(tipo) ? next.delete(tipo) : next.add(tipo); return next; })}
                  className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${tipologie.has(tipo) ? `${colors} ring-1 ring-current` : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}>
                  {tipo}
                </button>
              ))}
            </div>
            <label className={`mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition ${mostraSconti ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-white text-slate-600"}`}>
              <input type="checkbox" checked={mostraSconti} onChange={(event) => setMostraSconti(event.target.checked)} className="h-4 w-4 accent-emerald-600" />
              <span>Mostra sconti</span>
            </label>
          </div>
          <div className="rounded-xl border border-blue-100 bg-white px-5 py-3 text-right shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{sezioneEsplosa == null ? "Totale selezionato" : "Totale sezione"}</div>
            <div className="mt-0.5 text-2xl font-bold text-blue-700">{euro(totaleModale)}</div>
          </div>
        </div>
        <div className="max-h-[58vh] overflow-auto p-4">
          {sezioneEsplosa != null && <button type="button" onClick={() => setSezioneEsplosa(null)} className="mb-3 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">← Torna alle sezioni</button>}
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500">
              <tr className="border-b"><th className="px-3 py-2 text-left">{sezioneEsplosa == null ? "Sezione" : "Codice"}</th><th className="px-3 py-2 text-left">Descrizione</th><th className="px-3 py-2 text-right">Quantità</th><th className="px-3 py-2 text-right">Totale</th></tr>
            </thead>
            <tbody>{(sezioneEsplosa == null ? totaliPerSezione : totaliPerVoceSezione).map((row) => {
              const isZ = row.tipologie.has("Z"); const isI = row.tipologie.has("I"); const isB = row.tipologie.has("B");
              const rowClass = isB ? (isZ || isI ? `border-l-purple-500 bg-gradient-to-r ${isZ ? "from-blue-50/90" : "from-emerald-50/90"} ${isZ && isI ? "via-emerald-50/90" : ""} to-purple-50/90` : "border-l-purple-500 bg-purple-50/70") : isZ && isI ? "border-l-blue-500 bg-gradient-to-r from-blue-50/90 to-emerald-50/90" : isI ? "border-l-emerald-500 bg-emerald-50/70" : "border-l-blue-500 bg-blue-50/70";
              return <tr key={sezioneEsplosa == null ? row.sezione : row.codice} onClick={sezioneEsplosa == null ? () => setSezioneEsplosa(row.sezione) : undefined} className={`border-b border-l-4 border-gray-100 ${rowClass} ${sezioneEsplosa == null ? "cursor-pointer hover:brightness-95" : ""}`}>
              <td className={`px-3 py-2 font-semibold ${isB && !isZ && !isI ? "text-purple-700" : isI && !isZ ? "text-emerald-700" : "text-blue-700"}`}>{sezioneEsplosa == null ? row.sezione : row.codice}</td>
              <td className="px-3 py-2 text-gray-700">{row.descrizione}</td>
              <td className="px-3 py-2 text-right text-gray-700">{Number(row.quantita) === 0 ? "—" : quantita(row.quantita)}</td>
              <td className="whitespace-nowrap px-3 py-2 text-right font-semibold text-gray-900">{row.totale === 0 ? "—" : euro(row.totale)}</td>
            </tr>;})}</tbody>
          </table>
        </div>
      </div>
    </Modal>
  </>;
};
